// Implements: F-ITK-007
/**
 * The read and the two writes behind the WhatsApp linking section.
 *
 * **Keyed by person (checklist 14).** The routes are self-only, so the URL is the same for
 * everybody: the person's id in the key is what stops a second person signing in on the same tab
 * from being shown the first one's numbers, or an answer still in flight for the first.
 *
 * **The switch is the list.** While intake is disabled every linking route answers the
 * unknown-route `404`; that answer of the list is what hides the section and its nav entry
 * (`isIntakeDisabled`). Any other failure is an error to show, never a reason to hide.
 *
 * **The code never enters the query cache.** Issuing is a plain call, not a `useMutation` (whose
 * cache would keep the last answer for as long as the client lives): its answer goes back to the
 * caller, which holds it in component state only. Removal refetches the list, whose rows the
 * server derives.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { useCallback } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import type { IssueLinkCodeInput, IssuedLinkCodeView, SenderBindingView } from "../../../types/intake";
import { fetchBindings, issueLinkCode, unlinkBinding } from "../api/intakeApi";

export function intakeBindingsKey(personId: string): readonly ["intake", "bindings", string] {
  return ["intake", "bindings", personId];
}

/** The unknown-route `404` of a disabled intake. */
export function isIntakeDisabled(error: ApiError | null): boolean {
  return error?.status === 404;
}

/** @param personId The signed-in person, from the session: never typed by anybody. */
export function useBindings(personId: string, enabled = true): UseQueryResult<readonly SenderBindingView[], ApiError> {
  return useQuery<readonly SenderBindingView[], ApiError>({
    queryKey: intakeBindingsKey(personId),
    queryFn: ({ signal }) => fetchBindings(signal),
    enabled,
  });
}

/**
 * Whether the server answered the person's list: the nav entry is offered on that answer only.
 * @param enabled `false` asks nothing (no session yet) and answers `false`.
 */
export function useIntakeEnabled(personId: string, enabled = true): boolean {
  return useBindings(personId, enabled).isSuccess;
}

export type IssueOutcome =
  | { readonly ok: true; readonly issued: IssuedLinkCodeView }
  | { readonly ok: false; readonly error: ApiError };

export function useIssueLinkCode(): (input: IssueLinkCodeInput) => Promise<IssueOutcome> {
  return useCallback(async (input: IssueLinkCodeInput): Promise<IssueOutcome> => {
    try {
      return { ok: true, issued: await issueLinkCode(input) };
    } catch (cause) {
      return { ok: false, error: asApiError(cause) };
    }
  }, []);
}

export type UnlinkOutcome = { readonly ok: true } | { readonly ok: false; readonly error: ApiError };

export function useUnlinkBinding(personId: string): (bindingId: string) => Promise<UnlinkOutcome> {
  const queryClient = useQueryClient();

  return useCallback(
    async (bindingId: string): Promise<UnlinkOutcome> => {
      let outcome: UnlinkOutcome = { ok: true };
      try {
        await unlinkBinding(bindingId);
      } catch (cause) {
        outcome = { ok: false, error: asApiError(cause) };
      }
      // Either way the list is read again: a `404` means the row is already gone.
      void queryClient.invalidateQueries({ queryKey: intakeBindingsKey(personId) });
      return outcome;
    },
    [queryClient, personId],
  );
}
